/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { useEffect, useState } from 'react';
import axios from '../api/axios';

export type TableOperationManifestKey =
  | 'corporateOutput'
  | 'exportExcel'
  | 'exportCsv'
  | 'exportPdf'
  | 'downloadTemplate'
  | 'uploadTemplate'
  | 'importHistory'
  | 'fieldDictionary';

export interface TableOperationManifestItem {
  key: TableOperationManifestKey;
  name?: Record<string, string>;
  description?: Record<string, string>;
  disabledReason?: Record<string, string>;
  permissionCode?: string;
  supported: boolean;
  displayOrder?: number;
}

export interface TableOperationsManifest {
  tableKey: string;
  name?: Record<string, string>;
  operations: TableOperationManifestItem[];
}

interface ManifestWithTableOperations {
  tableOperations?: TableOperationsManifest[];
}

let tableOperationsCache: TableOperationsManifest[] | null = null;
let tableOperationsPromise: Promise<TableOperationsManifest[]> | null = null;

function unwrapApiData<T>(payload: unknown): T | null {
  if (!payload || typeof payload !== 'object') return null;
  const body = payload as { data?: unknown };
  return (body.data ?? payload) as T;
}

async function loadTableOperations(): Promise<TableOperationsManifest[]> {
  if (tableOperationsCache) return tableOperationsCache;
  if (!tableOperationsPromise) {
    tableOperationsPromise = axios
      .get('/api/manifest')
      .then((res) => {
        const manifest = unwrapApiData<ManifestWithTableOperations>(res.data);
        const operations = Array.isArray(manifest?.tableOperations) ? manifest.tableOperations : [];
        tableOperationsCache = operations;
        return operations;
      })
      .catch(() => {
        tableOperationsCache = [];
        return [];
      })
      .finally(() => {
        tableOperationsPromise = null;
      });
  }
  return tableOperationsPromise;
}

export function useTableOperationManifest(tableKey?: string): TableOperationsManifest | null {
  const [manifest, setManifest] = useState<TableOperationsManifest | null>(null);

  useEffect(() => {
    let cancelled = false;
    const normalizedKey = String(tableKey ?? '').trim();
    if (!normalizedKey) {
      setManifest(null);
      return () => {
        cancelled = true;
      };
    }

    loadTableOperations().then((operations) => {
      if (cancelled) return;
      setManifest(operations.find((entry) => entry.tableKey === normalizedKey) ?? null);
    });

    return () => {
      cancelled = true;
    };
  }, [tableKey]);

  return manifest;
}
